import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { FileSpreadsheet, Radio, Sparkles, UserPlus, Users, Activity } from "lucide-react";
import { fmtINR } from "@/lib/format";

const METRICS = [
  { k: "invoices_this_month",     label: "Sale invoices · this month", icon: FileSpreadsheet, tone: "orange" },
  { k: "gsp_api_calls",           label: "GST/GSP API calls",          icon: Radio,           tone: "blue" },
  { k: "ai_credits_used",         label: "AI credits used",            icon: Sparkles,        tone: "purple" },
  { k: "users_added_this_month",  label: "Users added · this month",   icon: UserPlus,        tone: "emerald" },
];

function MetricCard({ k, label, icon: Icon, tone, value, sub }) {
  const tones = {
    orange: "text-orange-500 bg-orange-500/10",
    blue:   "text-blue-500   bg-blue-500/10",
    purple: "text-purple-500 bg-purple-500/10",
    emerald:"text-emerald-500 bg-emerald-500/10",
  };
  return (
    <Card className="p-4 rounded-sm bg-card border-border text-foreground" data-testid={`usage-metric-${k}`}>
      <div className="flex items-start justify-between">
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
        <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${tones[tone]}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className="text-3xl font-bold mt-3">{typeof value === "number" ? value.toLocaleString("en-IN") : value ?? "—"}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-1 font-mono">{sub}</div>}
    </Card>
  );
}

/**
 * Super-Admin platform-wide usage metrics. Aggregates per-tenant counters
 * for the current calendar month + per-org breakdown table.
 */
export default function AdminUsage() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => { api.get("/admin/usage-metrics").then(r => setData(r.data)).catch(() => setData({ rows: [], totals: {} })); }, []);

  if (!data) return <div className="p-8 text-slate-500">Loading platform usage…</div>;
  const totals = data.totals || {};
  const rows = (data.rows || []).filter(r =>
    !q || `${r.org_name || ""} ${r.plan || ""}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="p-6 space-y-6" data-testid="admin-usage-page">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-orange-500">// Usage metrics</div>
        <h1 className="text-3xl font-bold mt-1 text-slate-900">Platform usage · {data.month}</h1>
        <div className="text-slate-500 text-sm mt-1">Aggregate counters across all {data.orgs_count} orgs on Zenith360-ERP.</div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {METRICS.map(m => (
          <MetricCard key={m.k} {...m} value={totals[m.k] || 0}
            sub={m.k === "invoices_this_month" ? `${(totals.invoices_total || 0).toLocaleString("en-IN")} lifetime` : null} />
        ))}
      </div>

      <Card className="rounded-sm bg-white border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-orange-500">// Per-entity</div>
            <div className="font-heading font-semibold text-lg mt-0.5 text-slate-900">Every tenant's usage</div>
          </div>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Filter by name / plan…"
            className="border border-slate-300 rounded-sm px-3 py-1.5 text-xs w-64" data-testid="usage-search" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="text-[10px] font-mono uppercase tracking-widest text-slate-500 border-b border-slate-200 bg-slate-50">
              <tr>
                <th className="text-left p-3">Entity</th>
                <th className="text-left p-3">Plan</th>
                <th className="text-right p-3">Invoices (MTD)</th>
                <th className="text-right p-3">GSP calls</th>
                <th className="text-right p-3">AI credits</th>
                <th className="text-right p-3">Users added</th>
                <th className="text-right p-3">Users total</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-500">No usage recorded yet this month.</td></tr>}
              {rows.map(r => (
                <tr key={r.org_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`usage-row-${r.org_id}`}>
                  <td className="p-3 font-medium text-slate-900">{r.org_name || r.org_id.slice(0, 8)}</td>
                  <td className="p-3 uppercase font-mono text-[11px] text-slate-600">{r.plan || "—"}</td>
                  <td className="p-3 text-right font-mono">{(r.invoices_this_month || 0).toLocaleString("en-IN")}</td>
                  <td className="p-3 text-right font-mono">{(r.gsp_api_calls || 0).toLocaleString("en-IN")}</td>
                  <td className="p-3 text-right font-mono">{(r.ai_credits_used || 0).toLocaleString("en-IN")}</td>
                  <td className="p-3 text-right font-mono">{(r.users_added_this_month || 0).toLocaleString("en-IN")}</td>
                  <td className="p-3 text-right font-mono">{(r.users_total || 0).toLocaleString("en-IN")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
